import React, { useEffect, useMemo, useRef } from 'react';
import { map, prop, length, isEmpty, trim, equals } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { DOCUMENT_HISTORY_FILTER_DEFAULTS } from '../../../../_CONST';
import useDocumentHistoryEntries from '../../../../hooks/useDocumentHistoryEntries';
import groupHistoryEntriesByMonth from '../../../../utils/groupHistoryEntriesByMonth';
import DocumentHistoryMonthHeader from './DocumentHistoryMonthHeader';
import DocumentHistoryDayHeader from './DocumentHistoryDayHeader';
import DocumentHistoryEntry from './DocumentHistoryEntry';
import DocumentHistoryListState from './DocumentHistoryListState';
import DocumentHistoryEmptyState from './DocumentHistoryEmptyState';
import DocumentHistoryFilteredEmptyState from './DocumentHistoryFilteredEmptyState';

const SCROLL_THRESHOLD_PX = 200;
const DEFAULT_PERIOD = prop('period', DOCUMENT_HISTORY_FILTER_DEFAULTS);
const DEFAULT_SORT = prop('sort', DOCUMENT_HISTORY_FILTER_DEFAULTS);

const isFilterActive = ({ search = '', targetType = null, period = DEFAULT_PERIOD, sort = DEFAULT_SORT } = {}) => {
  const hasSearch = isNotNilOrEmpty(trim(search));
  const hasTargetType = isNotNilOrEmpty(targetType);
  const hasPeriod = isNotNilOrEmpty(period) && !equals(period, DEFAULT_PERIOD);
  const hasSort = isNotNilOrEmpty(sort) && !equals(sort, DEFAULT_SORT);

  return hasSearch || hasTargetType || hasPeriod || hasSort;
};

const DocumentHistoryList = ({ documentId, filterState, onResetFilters, onEmptyStateChange }) => {
  const {
    entries,
    monthlyCounts,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    retry
  } = useDocumentHistoryEntries(documentId, filterState);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: load first page once on mount only
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const canLoadMore = () => !isLoadingMore && hasMore;
    const isNearBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      return scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD_PX;
    };

    const handleScroll = () => {
      if (canLoadMore() && isNearBottom()) loadMore();
    };

    // catches the case where the list is too short to overflow yet (footer already visible)
    const handleWheel = (event) => {
      if (event.deltaY > 0 && canLoadMore() && isNearBottom()) loadMore();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [isLoadingMore, hasMore, loadMore]);

  const computedFilterState = useMemo(
    () => ({ ...DOCUMENT_HISTORY_FILTER_DEFAULTS, ...(filterState || {}) }),
    [filterState]
  );
  const sort = prop('sort', computedFilterState);
  const searchTerm = prop('search', computedFilterState);

  const monthGroups = groupHistoryEntriesByMonth(
    entries,
    monthlyCounts,
    sort
  );
  const totalLoaded = length(entries);
  const isInitialLoad = isLoadingMore && isEmpty(entries);
  const hasActiveFilter = isFilterActive(computedFilterState);

  const isGenuinelyEmpty = !isLoadingMore && isEmpty(entries) && !error && !hasActiveFilter;

  // Only disable parent filters when there is truly no history at all.
  // Keep filters enabled while searching/filtering with no matches and during initial loading.
  useEffect(() => {
    if (onEmptyStateChange) onEmptyStateChange(isGenuinelyEmpty);
  }, [isGenuinelyEmpty, onEmptyStateChange]);

  const getFooterVariant = () => {
    if (error) return 'error';
    if (isLoadingMore) return 'loading';
    if (!hasMore) return 'end';
    return 'loaded';
  };

  return (
    <div ref={scrollContainerRef} className="mt-4 flex-1 min-h-0 overflow-y-auto scrollbar-none">
      {isInitialLoad && (
        <DocumentHistoryListState variant="loading" />
      )}

      {!isLoadingMore && isEmpty(entries) && !error && (
        hasActiveFilter
          ? <DocumentHistoryFilteredEmptyState onReset={onResetFilters} />
          : <DocumentHistoryEmptyState />
      )}

      {!isEmpty(entries) && (
        <div>
          {map(monthGroup => {
            const monthLabel = prop('monthLabel', monthGroup);
            const entryCount = prop('entryCount', monthGroup);
            const days = prop('days', monthGroup);
            const monthKey = prop('monthKey', monthGroup);

            return (
              <div key={monthKey}>
                <DocumentHistoryMonthHeader
                  monthLabel={monthLabel}
                  entryCount={entryCount}
                />
                <div>
                  {map(dayGroup => {
                    const dayKey = prop('dayKey', dayGroup);
                    const dayLabel = prop('dayLabel', dayGroup);
                    const dayEntries = prop('entries', dayGroup);

                    return (
                      <div key={dayKey} className="mb-6">
                        <DocumentHistoryDayHeader dayLabel={dayLabel} />
                        <div className="bg-white rounded-b-lg border border-gray-200 shadow-sm">
                          {map(entry => (
                            <DocumentHistoryEntry
                              key={prop('id', entry)}
                              entry={entry}
                              searchTerm={searchTerm}
                            />
                          ), dayEntries)}
                        </div>
                      </div>
                    );
                  }, days)}
                </div>
              </div>
            );
          }, monthGroups)}
        </div>
      )}

      {!isInitialLoad && !isEmpty(entries) && (
        <DocumentHistoryListState
          variant={getFooterVariant()}
          loadedCount={totalLoaded}
          onRetry={retry}
        />
      )}
    </div>
  );
};

export default DocumentHistoryList;
